import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SITE_GLASS_CARD } from '../constants/siteUi';
import {
  SAVED_DEAL_ANALYSES_CHANGED,
  SAVED_DEAL_ANALYSES_QUOTA_BYTES,
  deleteSavedDealAnalysis,
  getSavedDealAnalysesStorageBytes,
  listSavedDealAnalyses,
  type SavedDealAnalysisRecord,
} from '../lib/savedDealAnalyses';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatSavedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function subtitleFromRecord(rec: SavedDealAnalysisRecord): string | null {
  const cap = rec.analysis.returnsSummary?.goingInCapRatePct;
  const price = rec.analysis.assumptions?.purchasePrice;
  const units = rec.analysis.property?.units;
  const parts: string[] = [];
  if (typeof cap === 'number' && Number.isFinite(cap)) parts.push(`Going-in cap ${cap.toFixed(2)}%`);
  if (typeof units === 'number' && Number.isFinite(units)) parts.push(`${units} units`);
  if (typeof price === 'number' && Number.isFinite(price)) {
    parts.push(
      `Price ${new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price)}`
    );
  }
  return parts.length ? parts.join(' · ') : null;
}

export function InvestorSavedAnalyses() {
  const [list, setList] = useState<SavedDealAnalysisRecord[]>([]);
  const [bytesUsed, setBytesUsed] = useState(0);

  useEffect(() => {
    function sync(): void {
      setList(listSavedDealAnalyses());
      setBytesUsed(getSavedDealAnalysesStorageBytes());
    }
    sync();
    window.addEventListener(SAVED_DEAL_ANALYSES_CHANGED, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(SAVED_DEAL_ANALYSES_CHANGED, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const pct = SAVED_DEAL_ANALYSES_QUOTA_BYTES > 0 ? Math.min(100, (bytesUsed / SAVED_DEAL_ANALYSES_QUOTA_BYTES) * 100) : 0;

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <p className="font-display text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-[var(--home-miami-blue)]">
          Tools
        </p>
        <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--home-text-on-dark)] md:text-3xl">
          Saved analyses
        </h1>
        <p className="max-w-2xl font-body text-sm leading-relaxed text-[var(--home-muted-on-dark)]">
          View and manage your saved property analyses
        </p>
      </header>

      <section className={`${SITE_GLASS_CARD} p-5 md:p-6`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <h2 className="font-display text-sm font-bold text-[var(--home-text-on-dark)]">
              Enable cloud sync for cross-device access
            </h2>
            <p className="max-w-xl font-body text-sm text-[var(--home-muted-on-dark)]">
              Your analyses are saved locally in this browser. Enable cloud sync to access them from any device.
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
            <button
              type="button"
              disabled
              className="rounded-full bg-white/10 px-5 py-2.5 font-body text-sm font-medium text-[var(--home-muted-on-dark)]"
            >
              Enable cloud sync
            </button>
            <p className="flex items-center justify-end gap-1.5 font-body text-[0.7rem] text-amber-200/90">
              <span aria-hidden>🚧</span>
              Work in progress
            </p>
          </div>
        </div>
        <div className="mt-5 space-y-1.5">
          <div className="h-2 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-[var(--home-miami-blue)] transition-[width]"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="font-body text-xs text-[var(--home-miami-blue)]">
            {formatBytes(bytesUsed)} of {formatBytes(SAVED_DEAL_ANALYSES_QUOTA_BYTES)} used ({pct.toFixed(1)}%)
          </p>
        </div>
      </section>

      {list.length === 0 ? (
        <section
          className={`${SITE_GLASS_CARD} flex flex-col items-center justify-center px-6 py-16 text-center md:py-20`}
        >
          <div className="mb-4 text-4xl" aria-hidden>
            📊
          </div>
          <h2 className="font-display text-lg font-bold text-[var(--home-text-on-dark)]">No saved analyses yet</h2>
          <p className="mt-2 max-w-md font-body text-sm text-[var(--home-muted-on-dark)]">
            Start analyzing properties to save your results here. Your analyses will be stored locally on this device.
          </p>
          <Link
            to="/dashboard/analyzer"
            className="mt-8 rounded-full bg-[var(--home-miami-blue)] px-8 py-3 font-display text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white shadow-lg shadow-black/25 transition hover:brightness-110"
          >
            Start new analysis
          </Link>
        </section>
      ) : (
        <ul className="space-y-3">
          {list.map((rec) => {
            const sub = subtitleFromRecord(rec);
            return (
            <li key={rec.id} className={`${SITE_GLASS_CARD} p-4 md:p-5`}>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 space-y-1">
                  <h3 className="truncate font-display text-base font-semibold text-[var(--home-text-on-dark)]">
                    {rec.label}
                  </h3>
                  <p className="font-body text-xs text-[var(--home-muted-on-dark)]">Saved {formatSavedAt(rec.savedAt)}</p>
                  {sub ? (
                    <p className="font-body text-xs text-[var(--home-text-on-dark)]/80">{sub}</p>
                  ) : null}
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Link
                    to={`/dashboard/analyzer?load=${encodeURIComponent(rec.id)}`}
                    className="rounded-full bg-[var(--home-miami-blue)] px-5 py-2 font-body text-xs font-semibold text-white transition hover:brightness-110"
                  >
                    Open
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('Remove this saved analysis from this device?')) deleteSavedDealAnalysis(rec.id);
                    }}
                    className="rounded-full border border-white/15 px-5 py-2 font-body text-xs font-medium text-[var(--home-text-on-dark)] transition hover:bg-white/10"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
